import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Receipt,
  Check,
  X,
  Loader2,
  User,
  Calendar,
  DollarSign,
  Clock,
  AlertCircle,
  Eye,
  CheckCircle,
  XCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const ReceiptVerification = () => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  useEffect(() => {
    fetchPendingReceipts();
  }, []);

  const fetchPendingReceipts = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/payments/receipts/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceipts(response.data.receipts || []);
    } catch (error) {
      console.error("Failed to fetch receipts:", error);
      toast.error("Failed to load pending receipts");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (receiptId, action) => {
    setVerifying(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ action });
      if (action === "reject" && rejectionReason) {
        params.append("rejection_reason", rejectionReason);
      }

      await axios.post(
        `${API}/api/payments/receipt/${receiptId}/verify?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(
        action === "approve"
          ? "Payment verified! Appointment confirmed."
          : "Receipt rejected. Client will be notified."
      );

      setSelectedReceipt(null);
      setShowRejectDialog(false);
      setRejectionReason("");
      fetchPendingReceipts();
    } catch (error) {
      console.error("Verification error:", error);
      toast.error(error.response?.data?.detail || "Failed to process receipt");
    } finally {
      setVerifying(false);
    }
  };

  const getPaymentMethodName = (method) => {
    const names = {
      cashapp: "Cash App",
      zelle: "Zelle",
      paypal: "PayPal",
      cash: "Cash"
    };
    return names[method] || method;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  if (receipts.length === 0) {
    return (
      <Card className="bg-stone-800/50 border-stone-700">
        <CardContent className="p-8 text-center">
          <Receipt className="w-12 h-12 text-stone-500 mx-auto mb-4" />
          <h3 className="text-white font-medium mb-2">No Pending Receipts</h3>
          <p className="text-gray-400 text-sm">
            Receipts from clients using Cash App, Zelle, or PayPal will appear here for verification.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Receipt className="w-5 h-5 text-amber-400" />
          Pending Receipt Verification
        </h3>
        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
          {receipts.length} Pending
        </Badge>
      </div>

      <div className="grid gap-4">
        {receipts.map((receipt) => (
          <Card
            key={receipt.receipt_id}
            className="bg-stone-800/50 border-stone-700 hover:border-amber-500/30 transition-colors cursor-pointer"
            onClick={() => setSelectedReceipt(receipt)}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-white font-medium">
                      {receipt.client?.name || "Unknown Client"}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      ${receipt.amount?.toFixed(2)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(receipt.uploaded_at)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="border-purple-500/50 text-purple-400"
                  >
                    {getPaymentMethodName(receipt.payment_method)}
                  </Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-amber-400 hover:text-amber-300"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Receipt Detail Dialog */}
      <Dialog
        open={!!selectedReceipt}
        onOpenChange={() => setSelectedReceipt(null)}
      >
        <DialogContent className="bg-stone-900 border-stone-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Receipt className="w-5 h-5 text-amber-400" />
              Verify Payment Receipt
            </DialogTitle>
          </DialogHeader>

          {selectedReceipt && (
            <div className="space-y-4">
              {/* Client & Payment Info */}
              <div className="bg-stone-800 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Client</span>
                  <span className="text-white font-medium">
                    {selectedReceipt.client?.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Payment Method</span>
                  <Badge
                    variant="outline"
                    className="border-purple-500/50 text-purple-400"
                  >
                    {getPaymentMethodName(selectedReceipt.payment_method)}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Amount</span>
                  <span className="text-amber-400 font-bold text-lg">
                    ${selectedReceipt.amount?.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Uploaded</span>
                  <span className="text-gray-300">
                    {formatDate(selectedReceipt.uploaded_at)}
                  </span>
                </div>
                {selectedReceipt.appointment && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Service</span>
                    <span className="text-gray-300">
                      {selectedReceipt.appointment.service_name}
                    </span>
                  </div>
                )}
              </div>

              {/* Receipt Image */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-300">
                  Receipt Screenshot
                </h4>
                {selectedReceipt.receipt_image && (
                  <img
                    src={selectedReceipt.receipt_image}
                    alt="Payment receipt"
                    className="w-full rounded-lg border border-stone-700 max-h-80 object-contain bg-stone-800"
                  />
                )}
              </div>

              {/* Client Notes */}
              {selectedReceipt.notes && (
                <div className="bg-stone-800/50 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-gray-400 mb-1">
                    Client Notes
                  </h4>
                  <p className="text-gray-300 text-sm">
                    {selectedReceipt.notes}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                  onClick={() => setShowRejectDialog(true)}
                  disabled={verifying}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-semibold"
                  onClick={() =>
                    handleVerify(selectedReceipt.receipt_id, "approve")
                  }
                  disabled={verifying}
                >
                  {verifying ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Verify & Confirm
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="bg-stone-900 border-stone-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              Reject Receipt
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-gray-400 text-sm">
              Please provide a reason for rejecting this receipt. The client
              will be notified.
            </p>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="e.g., Amount doesn't match, receipt unclear, payment not found..."
              className="bg-stone-800 border-stone-700 text-white"
              rows={3}
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-stone-600"
                onClick={() => {
                  setShowRejectDialog(false);
                  setRejectionReason("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() =>
                  handleVerify(selectedReceipt?.receipt_id, "reject")
                }
                disabled={verifying}
              >
                {verifying ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <X className="w-4 h-4 mr-2" />
                )}
                Reject Receipt
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReceiptVerification;
